import type { TFunction } from "i18next";
import { useU2000FormStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import type { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { useState, useRef, useEffect } from "react";
import { U2000FormStateEnumEditMode } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { AxiosInstance } from "axios";
import { doLoadLogicalUnitActions, type U2104AffiliationLogicalUnit, type U2104LogicalUnit, type U2104LogicalUnitAction } from "../../../../U2104Service";
import { useU2104LoadLogicalUnitActionHandlers } from "../../u2104Role/U2104RoleActionTab/U2104UseLoadLogicalUnitActionHandlers";
import { generateTemporaryId } from "../../../../../u2000Core/tools/U2000IdUtils";

// Gestion des handlers pour les actions d'une unité logique
export interface U2104AffiliationActionHandler {
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;
  isDialogOpen: boolean;
  onDlgClose: (result: string) => void;
  isDeleteDialogOpen: boolean;
  onDlgDeleteClose: (result: string) => void;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U2104LogicalUnit>>;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U2104AffiliationLogicalUnit>>;
  dataChangedEvent: number;
  onLogicalUnitChangeObject: (logicalUnit: U2104LogicalUnit | null) => void;
  onCheckLogicalUnitAction: (action: U2104LogicalUnitAction) => void;
  logicalUnits: U2104LogicalUnit[];
  logicalUnitActions: U2104LogicalUnitAction[];
  gridLogicalUnitCodes: string[];
}

// Paramètres pour les handlers des actions d'une unité logique
export interface U2104AffiliationActionHandlerParam {
  u2000AxiosAuthenticated: AxiosInstance;
  orgId?: string;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U2104AffiliationLogicalUnit>>;
  t: TFunction;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U2104LogicalUnit>>;
}

// Gestion des handlers pour les actions d'une unité logique
export interface U2104AffiliationActionHandlerReturn {
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;

  onDlgClose: (result: string) => void;
  onLogicalUnitChangeObject: (logicalUnit: U2104LogicalUnit | null) => void;
  onCheckLogicalUnitAction: (action: U2104LogicalUnitAction) => void;
  isDialogOpen: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;

  isDeleteDialogOpen: boolean;
  onDlgDeleteClose: (result: string) => void;

  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U2104LogicalUnit>>;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U2104AffiliationLogicalUnit>>;
  dataChangedEvent: number;
  logicalUnits: U2104LogicalUnit[];
  logicalUnitActions: U2104LogicalUnitAction[];
  gridLogicalUnitCodes: string[];
}

// Implémentation des handlers pour les actions d'une unité logique
export function u2104AffiliationActionHandler(params: U2104AffiliationActionHandlerParam): U2104AffiliationActionHandlerReturn {
  const { u2000AxiosAuthenticated, orgId, gridStateHandlers, formStateHandlers } = params;
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [dataChangedEvent, setDataChangedEvent] = useState<number>(0);
  const [logicalUnits, setLogicalUnits] = useState<U2104LogicalUnit[]>([]);
  const [logicalUnitActions, setLogicalUnitActions] = useState<U2104LogicalUnitAction[]>([]);
  const [gridLogicalUnitCodes, setGridLogicalUnitCodes] = useState<string[]>([]);
  const [localEditMode, setLocalEditMode] = useState<U2000FormStateEnumEditMode>(U2000FormStateEnumEditMode.NONE);
  const localEditModeRef = useRef(localEditMode);

  useEffect(() => {
    localEditModeRef.current = localEditMode;
  }, [localEditMode]);

  // Catalogue filtré org — BL-0270
  const loadLogicalUnitActionHandlers = useU2104LoadLogicalUnitActionHandlers({
    doLoadLogicalUnitFn: () => {
      return doLoadLogicalUnitActions(u2000AxiosAuthenticated, orgId);
    },
    onSuccess: (logicalUnits: U2104LogicalUnit[]) => {
      setLogicalUnits(logicalUnits);
      if (localEditModeRef.current === U2000FormStateEnumEditMode.EDIT) {
        updateLogicalUnitFromSelectedRow(logicalUnits);
        setLocalEditMode(U2000FormStateEnumEditMode.NONE);
      }
    },
    onError: ({ error }) => {
      console.error("Error loading logical unit actions:", error);
    },
  });

  const onLogicalUnitChangeObject = (logicalUnit: U2104LogicalUnit | null): void => {
    if (!logicalUnit) {
      const newData = formStateHandlers.getData();
      newData.logicalUnitCode = "";
      newData.logicalUnitDescription = "";
      formStateHandlers.setData(newData);
      setLogicalUnitActions([]);
      return;
    }

    const newData = formStateHandlers.getData();
    newData.id = logicalUnit.id;
    newData.logicalUnitCode = logicalUnit.logicalUnitCode;
    newData.logicalUnitDescription = logicalUnit.logicalUnitDescription;

    formStateHandlers.setData(newData);
    setLogicalUnitActions(logicalUnit.logicalUnitActions);
  };

  const onCheckLogicalUnitAction = (action: U2104LogicalUnitAction): void => {
    const updatedActions = logicalUnitActions.map((act) => (act.id === action.id ? { ...act, idChecked: !act.idChecked } : act));
    setLogicalUnitActions(updatedActions);

    const newData = formStateHandlers.getData();
    newData.logicalUnitActions = updatedActions;
    formStateHandlers.setData(newData);
  };

  // Gestion de l'ajout d'une action d'unité logique
  const onAdd = (): void => {
    setGridLogicalUnitCodes(gridStateHandlers.getData().map((item) => item.logicalUnitCode));
    setLogicalUnitActions([]);
    if (logicalUnits.length === 0) {
      // Charger les actions d'une unité logique avant d'ajouter
      loadLogicalUnitActionHandlers.loadLogicalUnitActions();
    }
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    setDialogOpen(true);
  };

  // Gestion de la modification d'une action d'unité logique
  const onModify = (): void => {
    setLocalEditMode(U2000FormStateEnumEditMode.EDIT);
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    setDialogOpen(true);
    setLogicalUnitActions([]);
    if (logicalUnits.length === 0) {
      // Charger les actions d'une unité logique avant d'ajouter
      loadLogicalUnitActionHandlers.loadLogicalUnitActions();
      return;
    }
    updateLogicalUnitFromSelectedRow(logicalUnits);
    return;
  };

  // Gestion de la suppression d'une action d'unité logique
  const onDelete = (): void => {
    setDeleteDialogOpen(true);
    return;
  };

  // Gestion de la fermeture du dialog de modification/ajout
  const onDlgClose = (result: string): void => {
    if (result === "cancel") {
      gridStateHandlers.setErrors([]);
      formStateHandlers.cancelData();
      setDialogOpen(false);
    } else if (result === "save") {
      if (formStateHandlers.getData().logicalUnitActions.length !== 0) {
        const businessHandlers = formStateHandlers.getBusinessHandlers();
        if (businessHandlers && businessHandlers.validateForm) {
          const errors = businessHandlers.validateForm(formStateHandlers.getData());
          formStateHandlers.setErrors(errors);
          formStateHandlers.saveData();
          // Si des erreurs de validation existent, ne pas fermer le dialogue
          if (Object.keys(errors).length > 0) {
            return; // Ne pas fermer le dialogue si des erreurs de validation existent
          }
        }

        if (formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT) {
          const newData = formStateHandlers.getData();

          // Créer un nouvel objet RoleLogicalUnit à partir de newData
          const newRoleLogicalUnit: U2104AffiliationLogicalUnit = {
            id: newData.id,
            logicalUnitCode: newData.logicalUnitCode,
            logicalUnitDescription: newData.logicalUnitDescription,
            actions: Array.isArray(newData.logicalUnitActions) ? newData.logicalUnitActions.filter((action) => action.idChecked).map((action) => action.logicalUnitActionCode) : [],
          };

          gridStateHandlers.getData().forEach((item, index) => {
            if (item.id === newRoleLogicalUnit.id) {
              const updatedData = [...gridStateHandlers.getData()];
              updatedData[index] = newRoleLogicalUnit;
              gridStateHandlers.setData(updatedData);
            }
          });
          setDataChangedEvent(dataChangedEvent + 1);
        } else if (formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.ADD) {
          const newData = formStateHandlers.getData();

          // Créer un nouvel objet RoleLogicalUnit à partir de newData
          const newRoleLogicalUnit: U2104AffiliationLogicalUnit = {
            id: generateTemporaryId(),
            logicalUnitCode: newData.logicalUnitCode,
            logicalUnitDescription: newData.logicalUnitDescription,
            actions: Array.isArray(newData.logicalUnitActions) ? newData.logicalUnitActions.filter((action) => action.idChecked).map((action) => action.logicalUnitActionCode) : [],
          };

          // Ajouter le nouvel objet au gridStateHandlers
          gridStateHandlers.setData([...gridStateHandlers.getData(), newRoleLogicalUnit]);
          gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
          setDataChangedEvent(dataChangedEvent + 1);
        }
      }
    }
    setDialogOpen(false);
    return;
  };

  // Gestion de la fermeture du dialog de suppression
  const onDlgDeleteClose = (result: string): void => {
    if (result === "yes") {
      const selectedRows = gridStateHandlers.getSelectedRows();
      const remainingRows = gridStateHandlers.getData().filter((row) => !selectedRows.some((selected) => selected.id === row.id));
      gridStateHandlers.setData(remainingRows);

      gridStateHandlers.setTotalRows(remainingRows.length);
      setDataChangedEvent(dataChangedEvent + 1);
    }
    setDeleteDialogOpen(false);
    return;
  };

  return {
    onAdd,
    onModify,
    onDelete,
    onDlgClose,
    isDialogOpen,
    setDialogOpen,
    formStateHandlers,
    gridStateHandlers,
    isDeleteDialogOpen,
    onDlgDeleteClose,
    dataChangedEvent,
    logicalUnits,
    logicalUnitActions,
    onLogicalUnitChangeObject,
    onCheckLogicalUnitAction,
    gridLogicalUnitCodes,
  };

  // Mettre à jour le LogicalUnit dans le formStateHandlers à partir de la ligne sélectionnée dans le gridStateHandlers
  function updateLogicalUnitFromSelectedRow(logicalUnits: U2104LogicalUnit[]): void {
    const selectedRow = gridStateHandlers.getSelectedRows()[0];
    // Convertir le RoleLogicalUnit sélectionné en LogicalUnit en retrouvant le record complet
    const logicalUnit = logicalUnits.find((lu) => lu.logicalUnitCode === selectedRow.logicalUnitCode);
    let logicalUnitActions: U2104LogicalUnitAction[] = [];
    if (logicalUnit) {
      logicalUnit.id = selectedRow.id;
      logicalUnitActions = logicalUnit.logicalUnitActions;
      logicalUnitActions.forEach((action) => {
        action.idChecked = selectedRow.actions.includes(action.logicalUnitActionCode);
      });
      formStateHandlers.setData(logicalUnit);
      setLogicalUnitActions(logicalUnitActions);
    }
  }
}
